import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { TemplateForm, type TemplateFormData } from '../components/templates/TemplateForm';
import { useTemplate } from '../hooks/useTemplate';
import { useUpdateTemplate } from '../hooks/useUpdateTemplate';
import type { UpdateTemplateRequest } from '../../lib/types/template.types';

export function TemplateEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const updateMutation = useUpdateTemplate();

  // Fetch template data
  const { data: templateData, isLoading, isError } = useTemplate(id || '', {
    enabled: !!id,
  });

  const handleSubmit = async (formData: TemplateFormData) => {
    if (!id) return;

    try {
      const updateData: UpdateTemplateRequest = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        content: { blocks: formData.blocks },
        tags: formData.tags,
        is_favorite: formData.is_favorite,
      };

      await updateMutation.mutateAsync({
        id,
        data: updateData,
      });

      // Navigate back to templates list on success
      navigate('/templates');
    } catch (error) {
      console.error('Failed to update template:', error);
      // Error is handled by React Query and displayed via toast/notification
    }
  };

  const handleCancel = () => {
    navigate('/templates');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-8">
        <div className="bg-white shadow-sm sticky top-0 z-20">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
              </button>
              <h1 className="text-xl font-semibold text-[#212121]">
                Edit Template
              </h1>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B8E87]" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !templateData) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-8">
        <div className="bg-white shadow-sm sticky top-0 z-20">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
              </button>
              <h1 className="text-xl font-semibold text-[#212121]">
                Edit Template
              </h1>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              Template not found
            </h3>
            <p className="text-gray-600 mb-4">
              The template you're looking for doesn't exist or has been deleted.
            </p>
            <button
              onClick={handleCancel}
              className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors"
            >
              Back to Templates
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] pb-8">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">
              Edit Template
            </h1>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <TemplateForm
            initialData={templateData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={updateMutation.isPending}
            submitLabel="Update Template"
          />
        </div>
      </div>
    </div>
  );
}
